import { useAuth } from "../../context/AuthContext";
import { TimezoneSelect } from "../../components/ui/timezone-select";
import { useEffect, useMemo, useState } from "react";
import { CalendarClock, X } from "lucide-react";
import { Dropdown } from "../../components/ui/dropdown";
import { DatePicker } from "../../components/ui/date-picker";
import { combineDateTime, formatCallbackWhen } from "../../lib/time";
import { cn } from "../../lib/utils";
import type { Callback } from "./types";

const TIMEZONES = [
  { value: "Asia/Kolkata", label: "Asia/Kolkata (IST)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST)" },
  { value: "Europe/London", label: "Europe/London (GMT/BST)" },
  { value: "America/New_York", label: "America/New_York (ET)" },
  { value: "UTC", label: "UTC" },
] as const;

interface RescheduleModalProps {
  callback: Callback;
  onClose: () => void;
  onConfirm: (date: string, time: string, timezone: string) => Promise<void>;
}

const toInputDate = (d: Date) => d.toISOString().slice(0, 10);
const toInputTime = (d: Date) => d.toTimeString().slice(0, 5);

export function RescheduleModal({ callback, onClose, onConfirm }: RescheduleModalProps) {
  const { user } = useAuth() as any;
  const globalTz = user?.timezone;
  const [date, setDate] = useState(callback.callbackDate);
  const [time, setTime] = useState(callback.callbackTime);
  const [timezone, setTimezone] = useState(callback.timezone);
  const [saving, setSaving] = useState(false);

  /* Escape closes */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const target = useMemo(() => combineDateTime(date, time), [date, time]);
  const isPast = target.getTime() <= Date.now();
  const today = toInputDate(new Date());

  const quick = (d: Date) => { setDate(toInputDate(d)); setTime(toInputTime(d)); };
  const tomorrow = (hour: number) => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(hour, 0, 0, 0); return d;
  };
  const submit = async () => {
    setSaving(true);
    try {
      await onConfirm(date, time, timezone);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md rounded-2xl border border-line bg-elevated shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-line p-5">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold text-ink">
              <CalendarClock className="h-4 w-4 text-brand" /> Reschedule Callback
            </h2>
            <p className="mt-0.5 text-sm text-ink-2">
              {callback.contactName} · <span className="font-mono text-xs">{callback.scheduleId}</span>
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 p-5">
          {/* Quick picks */}
          <div className="flex gap-2">
            {[
              { label: "In 1 hour", d: () => new Date(Date.now() + 3_600_000) },
              { label: "Tomorrow 10:00", d: () => tomorrow(10) },
              { label: "Tomorrow 18:00", d: () => tomorrow(18) },
            ].map((q) => (
              <button
                key={q.label}
                onClick={() => quick(q.d())}
                className="rounded-full bg-soft px-3 py-1.5 text-xs font-semibold text-ink-2 transition-colors hover:bg-brand-soft hover:text-brand"
              >
                {q.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Date</span>
              <DatePicker
                value={date}
                onChange={setDate}
                minDate={today}
                placeholder="Pick date"
                className="w-full"
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Time</span>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full rounded-lg border border-line bg-elevated px-3 py-2 font-mono text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </label>
          </div>

          <Dropdown
            ariaLabel="Timezone"
            value={timezone}
            options={TIMEZONES}
            onChange={setTimezone}
          />

          {isPast && (
            <p className="rounded-lg bg-danger-soft px-3 py-2 text-xs font-semibold text-danger">
              Selected time is in the past. Pick a future slot.
            </p>
          )}

          <p className="text-xs text-ink-3">
            Currently: <span className="font-mono">{formatCallbackWhen(callback.callbackDate, callback.callbackTime, callback.timezone, globalTz, callback.scheduledForUtc)}</span>
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-line p-5">
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-soft">
            Cancel
          </button>
          <button
            disabled={isPast || saving}
            onClick={submit}
            className={cn(
              "rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors",
              isPast || saving ? "cursor-not-allowed bg-line text-ink-3" : "bg-brand hover:bg-brand-hover",
            )}
          >
            {saving ? "Saving..." : "Confirm Reschedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
